import {
  Clock,
  Flag,
  Globe,
  Lightbulb,
  PartyPopper,
  PawPrint,
  Search,
  Shapes,
  Smile,
  UtensilsCrossed,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePosicaoFlutuante } from '@/shared/hooks/usePosicaoFlutuante';
import { EMOJI_CATEGORIES } from '../lib/emoji-data';
import { buscarEmojis, carregarIndiceDeEmojis } from '../lib/emoji-search';

/** Todos os emojis do seletor, na ordem das categorias (base da busca). */
const TODOS_OS_EMOJIS = [...new Set(EMOJI_CATEGORIES.flatMap((c) => c.emojis))];

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  people: Smile,
  nature: PawPrint,
  food: UtensilsCrossed,
  activities: PartyPopper,
  travel: Globe,
  objects: Lightbulb,
  symbols: Shapes,
  flags: Flag,
};

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  EMOJI_CATEGORIES.map((c) => [c.id, c.label]),
);

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  /** Elemento ao qual o popover deve se ancorar verticalmente. Click fora dele fecha. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Abre acima do botão (composer, no rodapé) ou abaixo (topo de um modal). */
  placement?: 'top' | 'bottom';
  /**
   * Desenha o seletor no body, preso ao botão (`position: fixed`), e vira de
   * lado se não couber. Necessário quando o botão está num pai com rolagem
   * própria (corpo de modal) — ali o seletor saía cortado — ou colado numa
   * borda da tela.
   */
  flutuante?: boolean;
}

const LARGURA = 340;
const ALTURA = 380;

const RECENTS_KEY = 'chatplus.emoji.recents';
const RECENTS_MAX = 24;
const TAB_RECENTS = 'recents';

function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string').slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecents(list: string[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch {
    // localStorage cheio ou bloqueado — silencia, recentes é UX nice-to-have.
  }
}

/**
 * Picker de emojis nativo (sem libs).
 *
 * - Render TODAS as categorias numa lista contínua scrollável.
 * - Tabs no topo funcionam como scrollspy: ao rolar, a categoria visível ganha
 *   highlight automaticamente. Click numa tab faz scroll suave pra ela.
 * - `content-visibility: auto` em cada categoria permite ao browser pular
 *   layout/paint das que estão fora da viewport (~1500 botões no DOM total).
 * - Recentes em localStorage (max 24, MRU primeiro).
 */
export function EmojiPicker({
  open,
  onClose,
  onSelect,
  anchorRef,
  placement = 'top',
  flutuante = false,
}: Props) {
  const [recents, setRecents] = useState<string[]>(() => loadRecents());
  const [activeCategory, setActiveCategory] = useState<string>(TAB_RECENTS);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // Quando user clica numa tab, ignoramos updates do IntersectionObserver
  // por uns ms — senão o scrollIntoView dispara intersections que sobrescrevem
  // a categoria que o user acabou de escolher.
  const programmaticScrollRef = useRef(false);
  // Busca por palavra (igual WhatsApp). O índice em português só é baixado na
  // primeira busca — vide lib/emoji-search.ts.
  const [busca, setBusca] = useState('');
  const [indice, setIndice] = useState<Map<string, string> | null>(null);
  const [indiceFalhou, setIndiceFalhou] = useState(false);
  const buscaRef = useRef<HTMLInputElement>(null);
  const buscando = busca.trim().length > 0;
  const posicao = usePosicaoFlutuante(anchorRef, open && flutuante, {
    largura: LARGURA,
    altura: ALTURA,
    lado: placement,
  });

  useEffect(() => {
    if (!buscando || indice) return;
    let vivo = true;
    setIndiceFalhou(false);
    carregarIndiceDeEmojis()
      .then((i) => {
        if (vivo) setIndice(i);
      })
      .catch(() => {
        if (vivo) setIndiceFalhou(true);
      });
    return () => {
      vivo = false;
    };
  }, [buscando, indice]);

  // Click fora fecha (considera anchor + popover como "dentro")
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  // Scrollspy: detecta qual categoria está predominantemente visível e
  // atualiza o highlight da tab. `rootMargin -10% 0 -70% 0` mira o topo
  // do scroll (categoria que tá entrando), evitando flicker quando duas
  // seções pequenas caem juntas no viewport.
  useEffect(() => {
    if (!open) return;
    const root = scrollRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticScrollRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const top = visible[0];
        if (!top) return;
        const id = top.target.getAttribute('data-category-id');
        if (id) setActiveCategory(id);
      },
      { root, threshold: [0, 0.1, 0.3, 0.6, 1], rootMargin: '-10% 0px -70% 0px' },
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [open]);

  // Reseta scroll pro topo quando o popover (re)abre.
  useEffect(() => {
    if (!open) return;
    const root = scrollRef.current;
    if (root) root.scrollTop = 0;
    setActiveCategory(recents.length > 0 ? TAB_RECENTS : 'people');
    setBusca('');
    // Abre já com o cursor na busca (como no WhatsApp).
    requestAnimationFrame(() => buscaRef.current?.focus());
    // biome-ignore lint/correctness/useExhaustiveDependencies: só on open transition
  }, [open]);

  const handleTabClick = useCallback((id: string) => {
    const el = sectionRefs.current.get(id);
    if (!el) return;
    setActiveCategory(id);
    programmaticScrollRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Libera o observer depois que o scroll smooth terminou (~400ms é suficiente).
    setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 500);
  }, []);

  function handlePick(e: string): void {
    onSelect(e);
    setRecents((prev) => {
      const next = [e, ...prev.filter((x) => x !== e)].slice(0, RECENTS_MAX);
      saveRecents(next);
      return next;
    });
  }

  if (!open) return null;

  const resultados = buscando && indice ? buscarEmojis(busca, TODOS_OS_EMOJIS, indice) : [];

  // Constrói lista de seções: Recentes só aparece se houver itens.
  const sections: Array<{ id: string; label: string; emojis: string[] }> = [];
  if (recents.length > 0) {
    sections.push({ id: TAB_RECENTS, label: 'Recentes', emojis: recents });
  }
  for (const c of EMOJI_CATEGORIES) {
    sections.push({ id: c.id, label: c.label, emojis: c.emojis });
  }

  const painel = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Selecionar emoji"
      className={`flex h-[380px] w-[340px] flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-md ${
        flutuante
          ? 'fixed z-[60]'
          : `absolute left-0 z-30 ${
              placement === 'top' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]'
            }`
      }`}
      // Flutuante: até a posição ser medida (mesmo quadro), fica montado e
      // invisível — o scrollspy precisa das seções no DOM desde a abertura.
      {...(flutuante && {
        // `height` = o que cabe na tela (encolhe em tela baixa; a lista rola).
        style: posicao
          ? { top: posicao.top, left: posicao.left, height: posicao.altura }
          : { top: 0, left: 0, visibility: 'hidden' as const },
      })}
    >
      {/* Busca por palavra */}
      <div className="flex-shrink-0 border-n-200 border-b px-2 py-1.5">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={1.75}
            className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 text-n-400"
            aria-hidden="true"
          />
          <input
            ref={buscaRef}
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              // Enter insere o primeiro resultado.
              if (e.key === 'Enter' && resultados[0]) {
                e.preventDefault();
                handlePick(resultados[0]);
              }
            }}
            placeholder="Pesquisar emoji"
            aria-label="Pesquisar emoji"
            className="h-[32px] w-full rounded-md bg-n-100 pr-8 pl-8 text-[13px] text-n-900 outline-none placeholder:text-n-400 focus-visible:outline-none"
          />
          {busca && (
            <button
              type="button"
              onClick={() => {
                setBusca('');
                buscaRef.current?.focus();
              }}
              aria-label="Limpar busca"
              className="-translate-y-1/2 absolute top-1/2 right-1.5 flex h-6 w-6 items-center justify-center rounded-sm text-n-500 hover:bg-n-200"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Resultados da busca (as categorias ficam montadas e só escondidas,
          pra o scrollspy seguir funcionando ao limpar a busca) */}
      {buscando && (
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {!indice && !indiceFalhou && (
            <div className="px-1 py-6 text-center text-[12px] text-n-500">Carregando…</div>
          )}
          {indiceFalhou && (
            <div className="px-1 py-6 text-center text-[12px] text-n-500">
              Não foi possível carregar a busca. Tente de novo.
            </div>
          )}
          {indice && resultados.length === 0 && (
            <div className="px-1 py-6 text-center text-[12px] text-n-500">
              Nenhum emoji encontrado
            </div>
          )}
          {resultados.length > 0 && (
            <div className="grid grid-cols-8 gap-0.5">
              {resultados.map((e) => (
                <button
                  key={`busca-${e}`}
                  type="button"
                  onClick={() => handlePick(e)}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-sm text-[20px] leading-none hover:bg-n-100"
                  aria-label={`Inserir emoji ${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs (scrollspy) */}
      <div
        className={`flex flex-shrink-0 items-center gap-0.5 border-n-200 border-b bg-n-50 px-1.5 py-1 ${
          buscando ? 'hidden' : ''
        }`}
      >
        {recents.length > 0 && (
          <button
            type="button"
            onClick={() => handleTabClick(TAB_RECENTS)}
            aria-pressed={activeCategory === TAB_RECENTS}
            title="Recentes"
            className={`flex h-[30px] w-[34px] items-center justify-center rounded-sm ${
              activeCategory === TAB_RECENTS
                ? 'bg-n-0 text-brand-600 shadow-sm'
                : 'text-n-500 hover:bg-n-0'
            }`}
          >
            <Clock size={15} strokeWidth={1.75} />
          </button>
        )}
        {EMOJI_CATEGORIES.map((c) => {
          const Icon = CATEGORY_ICONS[c.id] ?? Smile;
          const isActive = activeCategory === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => handleTabClick(c.id)}
              aria-pressed={isActive}
              title={c.label}
              className={`flex h-[30px] w-[34px] items-center justify-center rounded-sm ${
                isActive ? 'bg-n-0 text-brand-600 shadow-sm' : 'text-n-500 hover:bg-n-0'
              }`}
            >
              <Icon size={15} strokeWidth={1.75} />
            </button>
          );
        })}
      </div>

      {/* Lista contínua de todas as categorias */}
      <div
        ref={scrollRef}
        className={`min-h-0 flex-1 overflow-y-auto p-2 ${buscando ? 'hidden' : ''}`}
      >
        {sections.map((s) => (
          <div
            key={s.id}
            ref={(el) => {
              sectionRefs.current.set(s.id, el);
            }}
            data-category-id={s.id}
            // content-visibility permite ao browser pular layout/paint quando fora
            // da viewport — performance OK mesmo com ~1500 botões no DOM total.
            // contain-intrinsic-size reserva o espaço estimado, evita scroll-jump.
            className="mb-3 [content-visibility:auto] [contain-intrinsic-size:auto_300px]"
          >
            <div className="mb-1 px-1 py-1 font-semibold text-[11px] text-n-500 uppercase tracking-wide">
              {s.id === TAB_RECENTS ? 'Recentes' : CATEGORY_LABELS[s.id] ?? s.label}
            </div>
            <div className="grid grid-cols-8 gap-0.5">
              {s.emojis.map((e, i) => (
                <button
                  key={`${s.id}-${e}-${i}`}
                  type="button"
                  onClick={() => handlePick(e)}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-sm text-[20px] leading-none hover:bg-n-100"
                  aria-label={`Inserir emoji ${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return flutuante ? createPortal(painel, document.body) : painel;
}
